// ── Session transcript status section (ROLE-GATED + GATED-DARK) ─────────────
// Wave 6 (Sessions). The transcript read (GET /sessions/:id/transcript) is
// SERVER role-gated: participant + cohort_lead get a hard 403; company_admin gets
// a REDACTED transcript; coach/staff get raw. Until ingest the transcript text is
// dark.
//
// This panel respects the gate UP FRONT: for participant + cohort_lead it renders
// ONLY an honest permission-denied state and NEVER fires the transcript read (so
// no content can leak, and the denial does not depend on the network). For the
// allowed roles it renders transcript STATUS only (available / pending ingest) —
// it does NOT render raw transcript content here (the transcript viewer is its own
// route; this is the detail's status panel). No transcript text is fabricated.

import { Alert, Badge, humanizeLabel, Module, ModuleBody, ModuleHead } from "@vjal/ui";

import { HonestState, useHonestQuery } from "../../../components/honest-state";
import { getCoachingServices } from "../../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../../hooks/coaching-query-keys";
import { normalizeLiveTranscript, type LiveTranscript } from "../../../services/coaching-transcript";
import type { CoachingRole } from "../../../domain/roles";

const services = getCoachingServices();

// Roles the SERVER hard-403s for transcript access. The frontend mirrors the gate
// so a denied role never even fires the read (fail-closed, content can't leak).
const TRANSCRIPT_DENIED_ROLES: readonly CoachingRole[] = ["participant", "cohort_lead"];

export function SessionTranscriptSection({ sessionId, role }: { sessionId: string; role: CoachingRole }) {
  const denied = TRANSCRIPT_DENIED_ROLES.includes(role);

  const query = useHonestQuery<LiveTranscript>(
    [...coachingQueryKeys.sessionDetail(sessionId), "transcript"],
    async () => normalizeLiveTranscript(await services.reports.sessionTranscript(sessionId)),
    { enabled: !denied }
  );

  if (denied) {
    return (
      <Module span={6}>
        <ModuleHead title="Transcript" subtitle="Transcript access follows your role." />
        <ModuleBody>
          <HonestState
            posture="permission-denied"
            title="Transcript not available to your role"
            message="Session transcripts are restricted to coaching staff. No transcript content is shown for your role."
          />
        </ModuleBody>
      </Module>
    );
  }

  const transcript = query.data;
  const redacted = transcript?.accessMode === "redacted" || transcript?.artifactStatus === "redacted" || transcript?.visibility === "company_redacted";
  const status = transcript?.available ? "ready" : transcript?.artifactStatus === "redacted" ? "redacted" : transcript?.artifactStatus === "forbidden" ? "forbidden" : "pending";

  return (
    <Module span={6}>
      <ModuleHead title="Transcript" subtitle="Transcript status — content is dark until the session is ingested." />
      <ModuleBody className="space-y-3">
        {query.posture !== null ? (
          <HonestState
            posture={query.posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={query.posture === "empty" ? "No transcript yet" : undefined}
            message={
              query.posture === "empty"
                ? "A transcript will appear here once the session is held and ingested."
                : undefined
            }
          />
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Badge variant={status === "ready" ? "success" : "warning"}>
                {humanizeLabel(status)}
              </Badge>
              {redacted ? <Badge variant="info">redacted for company admin</Badge> : null}
            </div>
            <Alert variant="info">
              Transcript status is shown here; the full transcript opens in its own viewer when ingest is complete.
              {redacted ? " Coach-private content is excluded from the company-admin view." : ""}
            </Alert>
          </>
        )}
      </ModuleBody>
    </Module>
  );
}
